import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import SearchIcon from '@mui/icons-material/Search';

import ConfirmDialog from '../common/ConfirmDialog';
import WaehrungField from './WaehrungField';
import { useAuth } from '../../hooks/useAuth';

import { de } from 'date-fns/locale';
import { format, parse } from 'date-fns';

import MonthPicker from '../common/MonthPicker';
import TableSkeleton from '../common/TableSkeleton';
import { useNotification } from '../../hooks/NotificationContext';
import { extractApiError } from '../../utils/errorUtils';
import eingangsbelegeService, {
  DuplicateCleanupPreview,
  Eingangsbeleg,
  InvoiceOwner,
  ProviderDefinition,
} from '../../services/eingangsbelegeService';

interface Props {
  providers: ProviderDefinition[];
  canManage: boolean;
  canViewAll: boolean;
  refreshKey: number;
  onChanged: () => void;
}

const statusColors: Record<string, 'default' | 'warning' | 'success' | 'error'> = {
  neu: 'default',
  klaerung: 'warning',
  verbucht: 'success',
  archiviert: 'error',
};

const currentMonth = () => format(new Date(), 'yyyy-MM');

function formatLeistungsmonat(month: string | null | undefined): string {
  if (!month || !/^\d{4}-(0[1-9]|1[0-2])$/.test(month)) return '–';
  return format(parse(`${month}-01`, 'yyyy-MM-dd', new Date()), 'MMMM yyyy', { locale: de });
}

const BelegeTab: React.FC<Props> = ({
  providers,
  canManage,
  canViewAll,
  refreshKey,
  onChanged,
}) => {
  const { showSuccess, showError } = useNotification();
  const { user } = useAuth();
  const [documents, setDocuments] = useState<Eingangsbeleg[]>([]);
  const [owners, setOwners] = useState<InvoiceOwner[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(50);
  const [month, setMonth] = useState(currentMonth());
  const [provider, setProvider] = useState('');
  const [owner, setOwner] = useState<number | ''>('');
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<number[]>([]);
  const [editing, setEditing] = useState<Eingangsbeleg | null>(null);
  const [saving, setSaving] = useState(false);

  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [documentMonth, setDocumentMonth] = useState('');
  const [grossAmount, setGrossAmount] = useState('');
  const [currency, setCurrency] = useState('');
  const [documentStatus, setDocumentStatus] = useState<Eingangsbeleg['bearbeitungsstatus']>('neu');
  const [notes, setNotes] = useState('');
  const [deleting, setDeleting] = useState<Eingangsbeleg | null>(null);
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [cleanupBusy, setCleanupBusy] = useState(false);
  const [cleanupPreview, setCleanupPreview] = useState<DuplicateCleanupPreview | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const response = await eingangsbelegeService.documents({
        leistungsmonat: month || undefined,
        anbieter_code: provider || undefined,
        owner_id: owner === '' ? undefined : owner,
        bearbeitungsstatus: status || undefined,
        search: search || undefined,
        page: page + 1,
        size,
      });
      setDocuments(response.items);
      setTotal(response.total);
      setSelected((ids) => ids.filter((id) => response.items.some((document) => document.id === id)));
    } finally {
      setLoading(false);
    }
  }, [month, provider, owner, status, search, page, size]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  useEffect(() => {
    if (!canViewAll) return;
    eingangsbelegeService.owners().then(setOwners).catch(() => setOwners([]));
  }, [canViewAll]);

  const openEdit = (document: Eingangsbeleg) => {
    setEditing(document);
    setInvoiceNumber(document.rechnungsnummer || '');
    setInvoiceDate(document.rechnungsdatum || '');
    setDocumentMonth(document.leistungsmonat || '');
    setGrossAmount(document.bruttobetrag?.toString() || '');
    setCurrency(document.waehrung || 'EUR');
    setDocumentStatus(document.bearbeitungsstatus);
    setNotes(document.bemerkung || '');
  };

  const canEditDocument = (document: Eingangsbeleg) =>
    canManage || document.owner?.id === user?.id;

  const save = async () => {
    if (!editing) return;
    try {
      setSaving(true);
      await eingangsbelegeService.updateMetadata(editing.id, {
        rechnungsnummer: invoiceNumber || null,
        rechnungsdatum: invoiceDate || null,
        leistungsmonat: documentMonth || null,
        bruttobetrag: grossAmount ? Number(grossAmount.replace(',', '.')) : null,
        waehrung: currency || null,
        bemerkung: notes || null,
      });
      if (canManage) {
        await eingangsbelegeService.updateStatus(editing.id, documentStatus, notes);
      }
      setEditing(null);
      await load();
      onChanged();
      showSuccess('Eingangsbeleg aktualisiert');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!deleting) return;
    try {
      setSaving(true);
      await eingangsbelegeService.remove(deleting.id);
      setDeleting(null);
      if (editing?.id === deleting.id) setEditing(null);
      await load();
      onChanged();
      showSuccess('Eingangsbeleg gelöscht');
    } catch {
      showError('Eingangsbeleg konnte nicht gelöscht werden');
    } finally {
      setSaving(false);
    }
  };

  const exportZip = async () => {
    await eingangsbelegeService.exportZip(
      selected.length
        ? { beleg_ids: selected }
        : {
            leistungsmonat: month || undefined,
            anbieter_code: provider || undefined,
            owner_id: owner === '' ? undefined : owner,
            status: status || undefined,
          },
    );
    showSuccess('ZIP-Export wurde erstellt');
  };

  const openCleanup = async () => {
    try {
      setCleanupBusy(true);
      setCleanupOpen(true);
      setCleanupPreview(await eingangsbelegeService.duplicatePreview());
    } catch {
      setCleanupOpen(false);
      showError('Duplikate konnten nicht geprüft werden');
    } finally {
      setCleanupBusy(false);
    }
  };

  const runCleanup = async () => {
    try {
      setCleanupBusy(true);
      const result = await eingangsbelegeService.cleanupDuplicates();
      setCleanupOpen(false);
      setCleanupPreview(null);
      try {
        await load();
        onChanged();
      } catch {
        // Archivierung ist durch, die Liste wird beim nächsten Laden aktualisiert.
      }
      showSuccess(
        result.archivieren
          ? `${result.archivieren} Duplikat(e) in ${result.gruppen} Gruppe(n) archiviert`
          : 'Keine Duplikate gefunden',
      );
    } catch (error) {
      const axiosError = error as { response?: unknown };
      if (!axiosError.response) {
        showError(extractApiError(error, 'Duplikate konnten nicht bereinigt werden'));
      }
    } finally {
      setCleanupBusy(false);
    }
  };

  const toggleAll = (checked: boolean) => {
    setSelected(checked ? documents.map((document) => document.id) : []);
  };

  const formatAmount = (document: Eingangsbeleg) =>
    document.bruttobetrag == null
      ? '–'
      : `${Number(document.bruttobetrag).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${document.waehrung || ''}`;

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <MonthPicker
          label="Leistungsmonat"
          value={month}
          allowEmpty
          testId="leistungsmonat-filter"
          onChange={(value) => { setMonth(value); setPage(0); }}
        />
        <TextField
          select
          size="small"
          label="Anbieter"
          value={provider}
          onChange={(event) => { setProvider(event.target.value); setPage(0); }}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">Alle</MenuItem>
          {providers.map((item) => <MenuItem key={item.code} value={item.code}>{item.label}</MenuItem>)}
        </TextField>
        {canViewAll && (
          <TextField
            select
            size="small"
            label="Eigentümer"
            value={owner}
            onChange={(event) => { setOwner(event.target.value === '' ? '' : Number(event.target.value)); setPage(0); }}
            sx={{ minWidth: 190 }}
          >
            <MenuItem value="">Alle</MenuItem>
            {owners.map((item) => (
              <MenuItem key={item.id} value={item.id}>{item.vorname} {item.nachname}</MenuItem>
            ))}
          </TextField>
        )}
        <TextField
          select
          size="small"
          label="Status"
          value={status}
          onChange={(event) => { setStatus(event.target.value); setPage(0); }}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="">Alle</MenuItem>
          <MenuItem value="neu">Neu</MenuItem>
          <MenuItem value="klaerung">Klärung</MenuItem>
          <MenuItem value="verbucht">Verbucht</MenuItem>
          <MenuItem value="archiviert">Archiviert</MenuItem>
          <MenuItem value="alle">Alle inkl. Archiv</MenuItem>
        </TextField>
        <TextField
          size="small"
          label="Suche"
          value={search}
          onChange={(event) => { setSearch(event.target.value); setPage(0); }}
          InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} /> }}
          sx={{ minWidth: 220, flex: 1 }}
        />
        <Button variant="outlined" startIcon={<FolderZipIcon />} onClick={exportZip} disabled={total === 0}>
          {selected.length ? `${selected.length} als ZIP` : 'Gefilterte als ZIP'}
        </Button>
        {canManage && (
          <Button variant="outlined" startIcon={<CleaningServicesIcon />} onClick={openCleanup} disabled={cleanupBusy}>
            Duplikate bereinigen
          </Button>
        )}
      </Box>

      {loading ? (
        <TableSkeleton rows={8} columns={canViewAll ? 9 : 8} />
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={documents.length > 0 && selected.length === documents.length}
                    indeterminate={selected.length > 0 && selected.length < documents.length}
                    onChange={(event) => toggleAll(event.target.checked)}
                  />
                </TableCell>
                <TableCell>Anbieter / Datei</TableCell>
                {canViewAll && <TableCell>Eigentümer</TableCell>}
                <TableCell>Monat / Datum</TableCell>
                <TableCell>Rechnungsnummer</TableCell>
                <TableCell align="right">Brutto</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Import</TableCell>
                <TableCell align="right">Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {documents.map((document) => (
                <TableRow key={document.id} hover selected={selected.includes(document.id)}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selected.includes(document.id)}
                      onChange={(event) => setSelected((ids) => (
                        event.target.checked ? [...ids, document.id] : ids.filter((id) => id !== document.id)
                      ))}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{document.anbieter_bezeichnung}</Typography>
                    <Typography variant="caption" color="text.secondary">{document.original_dateiname}</Typography>
                  </TableCell>
                  {canViewAll && (
                    <TableCell>
                      {document.owner ? `${document.owner.vorname} ${document.owner.nachname}` : '–'}
                    </TableCell>
                  )}
                  <TableCell>
                    {formatLeistungsmonat(document.leistungsmonat)}
                    {document.rechnungsdatum && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {new Date(document.rechnungsdatum).toLocaleDateString('de-DE')}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{document.rechnungsnummer || '–'}</TableCell>
                  <TableCell align="right">{formatAmount(document)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={statusColors[document.bearbeitungsstatus] || 'default'}
                      label={
                        document.bearbeitungsstatus === 'klaerung'
                          ? 'Klärung'
                          : document.bearbeitungsstatus.charAt(0).toUpperCase() + document.bearbeitungsstatus.slice(1)
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {document.import_quelle === 'browser_connector' ? 'Browser' : 'Manuell'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {new Date(document.importiert_am).toLocaleDateString('de-DE')}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="PDF herunterladen">
                      <IconButton aria-label="PDF herunterladen" component="a" href={eingangsbelegeService.downloadUrl(document.id)} size="small">
                        <DownloadIcon />
                      </IconButton>
                    </Tooltip>
                    {canEditDocument(document) && (
                      <>
                        <Tooltip title="Bearbeiten">
                          <IconButton aria-label="Eingangsbeleg bearbeiten" size="small" onClick={() => openEdit(document)}><EditIcon /></IconButton>
                        </Tooltip>
                        <Tooltip title="Löschen">
                          <IconButton aria-label="Eingangsbeleg löschen" size="small" onClick={() => setDeleting(document)}><DeleteIcon /></IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {documents.length === 0 && (
                <TableRow>
                  <TableCell colSpan={canViewAll ? 9 : 8} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary">Keine Eingangsbelege für diese Filter.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, value) => setPage(value)}
            rowsPerPage={size}
            onRowsPerPageChange={(event) => { setSize(Number(event.target.value)); setPage(0); }}
            rowsPerPageOptions={[25, 50, 100, 200]}
            labelRowsPerPage="Einträge pro Seite"
          />
        </TableContainer>
      )}

      <Dialog open={!!editing} onClose={() => setEditing(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Eingangsbeleg bearbeiten</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <TextField label="Rechnungsnummer" value={invoiceNumber} onChange={(event) => setInvoiceNumber(event.target.value)} />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField fullWidth label="Rechnungsdatum" type="date" value={invoiceDate} onChange={(event) => setInvoiceDate(event.target.value)} InputLabelProps={{ shrink: true }} />
            <MonthPicker
              label="Leistungsmonat"
              value={documentMonth}
              allowEmpty
              fullWidth
              size="medium"
              onChange={setDocumentMonth}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField fullWidth label="Bruttobetrag" value={grossAmount} onChange={(event) => setGrossAmount(event.target.value)} />
            <WaehrungField value={currency} onChange={setCurrency} />
          </Box>
          {canManage && (
            <TextField select label="Buchhaltungsstatus" value={documentStatus} onChange={(event) => setDocumentStatus(event.target.value as Eingangsbeleg['bearbeitungsstatus'])}>
              <MenuItem value="neu">Neu</MenuItem>
              <MenuItem value="klaerung">Klärung</MenuItem>
              <MenuItem value="verbucht">Verbucht</MenuItem>
              <MenuItem value="archiviert">Archiviert</MenuItem>
            </TextField>
          )}
          <TextField label="Bemerkung" value={notes} onChange={(event) => setNotes(event.target.value)} multiline minRows={3} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Abbrechen</Button>
          <Button variant="contained" onClick={save} disabled={saving}>{saving ? 'Speichert…' : 'Speichern'}</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleting}
        title="Eingangsbeleg löschen"
        message={`„${deleting?.original_dateiname || 'Dieser Beleg'}“ wird einschließlich der PDF-Datei gelöscht.`}
        onConfirm={remove}
        onCancel={() => setDeleting(null)}
      />

      <Dialog open={cleanupOpen} onClose={() => !cleanupBusy && setCleanupOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Duplikate bereinigen</DialogTitle>
        <DialogContent>
          {cleanupBusy && !cleanupPreview ? (
            <Typography color="text.secondary">Duplikate werden geprüft…</Typography>
          ) : cleanupPreview && cleanupPreview.archivieren === 0 ? (
            <Typography>Es wurden keine doppelten Eingangsbelege gefunden.</Typography>
          ) : (
            <>
              <Typography sx={{ mb: 2 }}>
                {cleanupPreview?.archivieren} Beleg(e) in {cleanupPreview?.gruppen} Gruppe(n)
                werden archiviert. Der jeweils vollständigste Eintrag bleibt erhalten.
              </Typography>
              {(cleanupPreview?.groups || []).map((group) => (
                <Box key={`${group.anbieter_konto_id}-${group.keeper_id}`} sx={{ mb: 1.5 }}>
                  <Typography variant="subtitle2">{group.anbieter_bezeichnung}</Typography>
                  {group.items.map((item) => (
                    <Typography key={item.beleg_id} variant="body2" color="text.secondary">
                      {item.beleg_id === group.keeper_id ? 'Behalten' : 'Archivieren'}
                      {': '}
                      #{item.beleg_id}
                      {' · '}
                      {item.original_dateiname}
                      {item.rechnungsnummer ? ` · ${item.rechnungsnummer}` : ''}
                    </Typography>
                  ))}
                </Box>
              ))}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCleanupOpen(false)} disabled={cleanupBusy}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={runCleanup}
            disabled={cleanupBusy || !cleanupPreview?.archivieren}
          >
            {cleanupBusy ? 'Bereinigt…' : 'Jetzt archivieren'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BelegeTab;
